import { useState, useEffect } from 'react';
import { useAdminAuth } from './AdminAuthContext';
import { useAdminTheme } from './AdminThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import {
  AlertTriangle,
  Search,
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Copy,
  Eye,
  Link2,
  Building2,
  ArrowLeftRight,
  MessageSquare
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SMSReviewQueue = () => {
  const { isDarkMode } = useAdminTheme();
  const { canReviewTransactions } = useAdminAuth();
  const [reviewQueue, setReviewQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSMS, setSelectedSMS] = useState(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchReviewQueue();
  }, []);

  const fetchReviewQueue = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/sms/review-queue`, { withCredentials: true });
      setReviewQueue(response.data);
    } catch (error) {
      console.error('Error fetching review queue:', error);
      toast.error('Failed to load review queue');
    } finally {
      setLoading(false);
    }
  };

  const openActionModal = (sms, type) => {
    setSelectedSMS(sms);
    setActionType(type);
    setSelectedTransaction('');
    setRejectReason('');
    setActionModalOpen(true);
  };

  const handleAction = async () => {
    if (!selectedSMS) return;
    
    setActionLoading(true);
    try {
      const payload = {
        action: actionType
      };
      
      if (actionType === 'match') {
        if (!selectedTransaction) {
          toast.error('Please select a transaction to match');
          setActionLoading(false);
          return;
        }
        payload.transaction_id = selectedTransaction;
      } else if (actionType === 'reject') {
        payload.reason = rejectReason || 'Rejected by reviewer';
      }
      
      await axios.post(`${API}/admin/sms/${selectedSMS.sms_id}/action`, payload, { withCredentials: true });
      
      toast.success(
        actionType === 'match' ? 'SMS matched to transaction' :
        actionType === 'reject' ? 'SMS rejected' :
        'SMS marked as duplicate'
      );
      
      setActionModalOpen(false);
      fetchReviewQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredQueue = reviewQueue.filter(sms => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      sms.sms_id?.toLowerCase().includes(query) ||
      sms.raw_message?.toLowerCase().includes(query) ||
      sms.parsed_reference?.toLowerCase().includes(query) ||
      sms.phone_number?.includes(query)
    );
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US').format(amount);
  };

  if (!canReviewTransactions()) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-testid="review-restricted">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h3 className="text-gray-900 font-medium mb-2">Access Restricted</h3>
        <p className="text-gray-500">Only Super Admins and Reviewers can access the SMS Review Queue</p>
      </div>
    );
  }

  return (
    <div data-testid="sms-review-queue-page" className="animate-fadeIn">
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">SMS Review Queue</h1>
          <p className="text-gray-500 text-sm mt-1">Review and match unmatched SMS messages to transactions</p>
        </div>
        <Button
          variant="outline"
          className="border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200 active:scale-95"
          onClick={fetchReviewQueue}
          data-testid="refresh-queue-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2 transition-transform duration-300 hover:rotate-180" />
          Refresh
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-800 font-medium">Manual Review Required</p>
            <p className="text-blue-600 text-sm mt-1">
              These SMS messages could not be automatically matched to pending transactions. 
              Review the parsed data and potential matches below to manually confirm, reject, or mark as duplicate.
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          placeholder="Search SMS..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white border-gray-300 text-gray-900"
          data-testid="review-search-input"
        />
      </div>

      {/* Review Queue */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : filteredQueue.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-gray-900 font-medium mb-2">All caught up!</h3>
          <p className="text-gray-500">No SMS messages require manual review</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredQueue.map((sms, idx) => (
            <div
              key={sms.sms_id}
              className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:border-teal-200 hover:shadow-md transition-all duration-300"
              style={{ animationDelay: `${idx * 50}ms` }}
              data-testid={`review-item-${sms.sms_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">{sms.phone_number}</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      sms.network === 'MTN' ? 'bg-yellow-50 text-yellow-700' : 'bg-orange-50 text-orange-700'
                    }`}>
                      {sms.network}
                    </span>
                  </div>
                </div>
                <span className="text-gray-500 text-sm">{formatDate(sms.received_at)}</span>
              </div>

              {/* Raw Message */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-gray-700 text-sm font-mono">{sms.raw_message}</p>
              </div>

              {/* Parsed Data */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500 text-xs">Amount</span>
                  <p className="text-gray-900 font-semibold">
                    {sms.parsed_amount ? `LRD ${formatCurrency(sms.parsed_amount)}` : '-'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500 text-xs">Reference</span>
                  <p className="text-gray-900 font-mono text-sm">{sms.parsed_reference || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500 text-xs">Sender</span>
                  <p className="text-gray-900 text-sm">{sms.parsed_sender || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500 text-xs">SMS ID</span>
                  <p className="text-gray-900 font-mono text-sm">{sms.sms_id}</p>
                </div>
              </div>

              {/* Potential Matches */}
              {sms.potential_matches && sms.potential_matches.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Potential Matches</h4>
                  <div className="space-y-2">
                    {sms.potential_matches.map((match, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-50 rounded-lg p-3 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-700 text-sm">{match.merchant_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <ArrowLeftRight className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-900 font-mono text-sm">{match.transaction.transaction_id}</span>
                          </div>
                          <span className="text-gray-900">LRD {formatCurrency(match.transaction.amount)}</span>
                          <span className="text-gray-500 text-sm">{match.transaction.reference}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              match.score >= 100 ? 'bg-emerald-50 text-emerald-700' :
                              match.score >= 50 ? 'bg-amber-50 text-amber-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              Score: {match.score}
                            </span>
                            <p className="text-gray-400 text-xs mt-1">{match.match_reasons.join(', ')}</p>
                          </div>
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => {
                              setSelectedSMS(sms);
                              setSelectedTransaction(match.transaction.transaction_id);
                              setActionType('match');
                              setActionModalOpen(true);
                            }}
                            data-testid={`match-btn-${match.transaction.transaction_id}`}
                          >
                            <Link2 className="w-4 h-4 mr-1" />
                            Match
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Matches */}
              {(!sms.potential_matches || sms.potential_matches.length === 0) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-amber-700 text-sm">No potential transaction matches found</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200 active:scale-95"
                  onClick={() => openActionModal(sms, 'match')}
                  data-testid={`manual-match-${sms.sms_id}`}
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Manual Match
                </Button>
                <Button
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50 transition-all duration-200 active:scale-95"
                  onClick={() => openActionModal(sms, 'reject')}
                  data-testid={`reject-${sms.sms_id}`}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  variant="outline"
                  className="border-gray-300 text-gray-500 hover:bg-gray-50 transition-all duration-200 active:scale-95"
                  onClick={() => openActionModal(sms, 'duplicate')}
                  data-testid={`duplicate-${sms.sms_id}`}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Mark Duplicate
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Modal */}
      <Dialog open={actionModalOpen} onOpenChange={setActionModalOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'match' && 'Match SMS to Transaction'}
              {actionType === 'reject' && 'Reject SMS'}
              {actionType === 'duplicate' && 'Mark as Duplicate'}
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              {actionType === 'match' && 'Select the transaction to link with this SMS message'}
              {actionType === 'reject' && 'Provide a reason for rejecting this SMS'}
              {actionType === 'duplicate' && 'This SMS will be marked as a duplicate entry'}
            </DialogDescription>
          </DialogHeader>

          {selectedSMS && (
            <div className="py-4 space-y-4">
              {/* SMS Summary */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-700 text-sm">
                  <strong>Amount:</strong> {selectedSMS.parsed_amount ? `LRD ${formatCurrency(selectedSMS.parsed_amount)}` : '-'}
                </p>
                <p className="text-gray-700 text-sm">
                  <strong>Reference:</strong> {selectedSMS.parsed_reference || '-'}
                </p>
              </div>

              {actionType === 'match' && (
                <div>
                  <label className="text-sm text-gray-600 mb-2 block">Transaction ID</label>
                  <Input
                    placeholder="Enter transaction ID (e.g., FL-20260313-ABC123)"
                    value={selectedTransaction}
                    onChange={(e) => setSelectedTransaction(e.target.value)}
                    className="bg-white border-gray-300 text-gray-900 font-mono"
                    data-testid="transaction-id-input"
                  />
                  {selectedSMS.potential_matches && selectedSMS.potential_matches.length > 0 && (
                    <div className="mt-3">
                      <label className="text-sm text-gray-600 mb-2 block">Or select from suggestions:</label>
                      <Select value={selectedTransaction} onValueChange={setSelectedTransaction}>
                        <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                          <SelectValue placeholder="Select transaction" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {selectedSMS.potential_matches.map((match) => (
                            <SelectItem 
                              key={match.transaction.transaction_id} 
                              value={match.transaction.transaction_id}
                              className="text-gray-700"
                            >
                              {match.transaction.transaction_id} - LRD {formatCurrency(match.transaction.amount)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {actionType === 'reject' && (
                <div>
                  <label className="text-sm text-gray-600 mb-2 block">Reason (optional)</label>
                  <Textarea
                    placeholder="Enter reason for rejection..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="bg-white border-gray-300 text-gray-900"
                    data-testid="reject-reason-input"
                  />
                </div>
              )}

              {actionType === 'duplicate' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-700 text-sm">
                    This SMS will be marked as a duplicate and removed from the review queue.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setActionModalOpen(false)} className="text-gray-500">
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={actionLoading || (actionType === 'match' && !selectedTransaction)}
              className={
                actionType === 'match' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' :
                actionType === 'reject' ? 'bg-red-600 hover:bg-red-700 text-white' :
                'bg-gray-600 hover:bg-gray-700 text-white'
              }
              data-testid="confirm-action-btn"
            >
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {actionType === 'match' && 'Confirm Match'}
              {actionType === 'reject' && 'Reject SMS'}
              {actionType === 'duplicate' && 'Mark as Duplicate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SMSReviewQueue;
